import React, { useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import { View, Text, StyleSheet, Alert, ScrollView, TouchableOpacity, Linking } from 'react-native';
import { SOPORTE_WHATSAPP, SOPORTE_TELEFONO } from '../constants/contacto';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import Button3D from './Button3D';
import { pesos } from '../utils/dinero';
import { callPagarMultaConSaldo } from '../services/cloudFunctions';
import { mensajeDeError } from '../utils/mensajeDeError';
import { COBROS_EN_LA_APP } from '../constants/featureFlags';

/**
 * PENALTY GATE — Overlay bloqueante que aparece cuando el usuario tiene una
 * multa por cancelaciones pendiente (penaltyBlockActive=true). Le explica por
 * qué está bloqueado y le deja pagar desde su billetera o su tarjeta guardada.
 *
 * El bloqueo REAL es server-side (firestore.rules impide crear viajes/ofertas),
 * esto es la capa de UX: explicar + cobrar. Al pagar, el listener del propio
 * doc /users/{uid} refleja penaltyBlockActive=false y el overlay desaparece.
 *
 * Se monta en las pantallas de interacción (mapa). Mientras no haya multa, no
 * renderiza nada (retorna null) — cero impacto visual/funcional en el 99% de
 * los casos.
 */
export function PenaltyGate() {
  const colors = useThemeColors();
  const router = useRouter();
  const uid = useAppStore((s) => s.userFirebaseUid);
  const [blocked, setBlocked] = useState(false);
  const [fine, setFine] = useState(0);
  const [count, setCount] = useState(0);

  // EL SALDO QUE LA PERSONA YA TIENE EN LA APP. Con él se puede pagar la multa
  // sin salir a ninguna parte — es dinero suyo que ya está dentro.
  const saldo = useAppStore((s) => s.walletBalance);
  const [pagando, setPagando] = useState(false);
  const [pagada, setPagada] = useState(false);

  useEffect(() => {
    if (!uid) return;
    const unsub = firestoreService.listenToUserPenalty(uid, (p) => {
      setBlocked(p.penaltyBlockActive);
      setFine(p.cancellationFineOwed);
      setCount(p.cancellationCount);
    });
    return () => {
      try { (unsub as any)?.(); } catch {}
    };
  }, [uid]);

  // COBRO DE LA MULTA — temporalmente por soporte, no automático.
  //
  // Antes esto llamaba a `/api/penalty/pay-fine` en Railway. Railway fue
  // BORRADO: los dos botones fallaban siempre y este overlay NO tenía botón
  // de cerrar. Resultado: quien llegaba a 5 cancelaciones quedaba encerrado
  // en una pantalla completa, para siempre, sin forma de salir ni de pagar.
  // Un overlay sin salida es motivo de rechazo en Play Store por sí solo.
  //
  // Hasta que exista la Cloud Function de cobro, la multa se resuelve por
  // WhatsApp con el equipo. Es honesto y desbloquea a la persona de verdad.
  /**
   * PAGAR CON EL SALDO QUE YA TIENE.
   *
   * El cobro lo hace el SERVIDOR (`pagarMultaConSaldo`): `walletBalance` está
   * cerrado al cliente en firestore.rules, y así tiene que seguir — si el
   * teléfono pudiera tocarlo, cualquiera se pondría la multa en cero.
   *
   * NO desbloquea, y el texto lo dice. Lo pidió así el fundador: el pago es una
   * condición, no la absolución. Él mira el caso y reactiva desde el panel.
   * Prometer aquí que se abre al instante sería mentir dos minutos antes de que
   * la persona lo compruebe.
   */
  const pagarConSaldo = async () => {
    if (pagando) return;
    setPagando(true);
    try {
      const r = await callPagarMultaConSaldo({} as any);
      setPagada(true);
      setFine(0);
      Alert.alert(
        'Multa pagada',
        (r?.data as any)?.yaEstaba
          ? 'Tu multa ya estaba saldada. Estamos revisando tu cuenta para reactivarla.'
          : 'Cobramos tu multa de tu saldo. Esto existe porque cuando cancelas con un chofer ya en camino, él gastó tiempo y gasolina que nadie le devuelve. Revisamos tu cuenta y la reactivamos enseguida.'
      );
    } catch (e: any) {
      Alert.alert('No se pudo pagar', mensajeDeError(e, 'Inténtalo de nuevo en un momento.'));
    } finally {
      setPagando(false);
    }
  };

  const contactarSoporte = () => {
    const mensaje = encodeURIComponent(
      `Hola, tengo mi cuenta de Go Safely en pausa por una multa de ${pesos(fine)}. Quiero resolverla.`
    );
    // El número real vive en un solo sitio (src/constants/contacto.ts). Aquí
    // estaba escrito a mano el 809-555-0000, que NO EXISTE: es el equivalente
    // dominicano del "555" de las películas. La única salida que la app le
    // ofrecía a una persona bloqueada era llamar a un número inventado.
    Linking.openURL(`https://wa.me/${SOPORTE_WHATSAPP}?text=${mensaje}`).catch(() => {
      Alert.alert(
        'Escríbenos por WhatsApp',
        `No pudimos abrir WhatsApp. Escríbenos al ${SOPORTE_TELEFONO} para resolver tu multa de ${pesos(fine)}.`
      );
    });
  };

  if (!blocked) return null;

  return (
    <View style={styles.overlay}>
      <View style={[styles.card, { backgroundColor: colors.card }]}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={styles.iconCircle}>
            <Ionicons name="alert-circle" size={40} color="#FFF" />
          </View>
          <Text style={[styles.title, { color: colors.text }]}>Cuenta en pausa</Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            Registramos {count} cancelaciones después de que la otra persona ya se había comprometido.
            En Go Safely el tiempo de todos vale: para seguir usando la app, paga tu multa.
          </Text>

          <View style={[styles.fineBox, { borderColor: colors.border }]}>
            <Text style={[styles.fineLabel, { color: colors.textSecondary }]}>Multa pendiente</Text>
            <Text style={styles.fineAmount}>{pesos(fine)}</Text>
            <Text style={[styles.fineNote, { color: colors.textSecondary }]}>
              20% de la tarifa de cada viaje/pedido que cancelaste.
            </Text>
          </View>

          <View style={{ gap: 10, marginTop: 6 }}>
            {/* PRIMERO LO QUE SE RESUELVE SOLO. Si tiene saldo de sobra, no hay
                por qué mandarlo a escribir a nadie. */}
            {!pagada && fine > 0 && Number(saldo ?? 0) >= fine && (
              <Button3D
                title={pagando ? 'Cobrando…' : `Pagar ${pesos(fine)} con mi saldo`}
                color="#1B7F3B"
                onPress={pagarConSaldo}
              />
            )}
            {/* ══════════════════════════════════════════════════════════
                EL PASAJERO NO RECARGA LA APP — HAY QUE LLEVARLO
                ══════════════════════════════════════════════════════════
                Corrección del fundador (1-oct-2026): «el pasajero no recarga la
                app; el descuento del saldo aplica para el chofer, pero el
                pasajero tendría que pagarlo ya sea vía tarjeta o transferencia
                para volver a activarle la cuenta».
                Y tiene razón: un chofer vive con saldo dentro (ahí cobra y de
                ahí paga su cuota), pero un pasajero normal tiene cero, así que
                el botón de «pagar con mi saldo» no le sirve de nada.
                La buena noticia es que el camino YA EXISTE y no hace falta otro:
                la billetera recarga con TARJETA (Stripe) y con TRANSFERENCIA
                (comprobante que aprueba el admin), y sirve igual al pasajero —
                le dice «Saldo Disponible» en vez de «Saldo a favor»
                (src/app/wallet.tsx). Lo único que faltaba era llevarlo hasta
                ahí en vez de dejarlo mirando una cifra que no puede juntar.
                Un rail de cobro aparte solo para la multa sería una segunda
                tubería de dinero que mantener, sin conciliador y sin auditoría. */}
            {/* ⚠️ EN iOS ESTE BLOQUE NO SALE.
                Lleva a /wallet a recargar con tarjeta para desbloquear la
                cuenta: una llamada a la accion hacia un medio de pago que no
                es el de Apple, para desbloquear funciones de la app. Es
                exactamente lo que prohibe la 3.1.1.
                El boton de «pagar con mi saldo» de arriba SI se queda: ese
                saldo ya es suyo —en iOS solo puede venir de bonos y
                compensaciones, porque la recarga esta cerrada— asi que
                gastarlo no es comprar nada. */}
            {COBROS_EN_LA_APP && !pagada && fine > 0 && Number(saldo ?? 0) < fine && (
              <>
                <Button3D
                  title={`Pagar ${pesos(fine)} con tarjeta o transferencia`}
                  color="#2196F3"
                  onPress={() => {
                    setBlocked(false); // sin esto el overlay tapa la billetera
                    router.push('/wallet' as any);
                  }}
                />
                <Text style={[styles.fineNote, { color: colors.textSecondary, textAlign: 'center' }]}>
                  {Number(saldo ?? 0) > 0
                    ? `Tienes ${pesos(Number(saldo ?? 0))}. Recarga ${pesos(fine - Number(saldo ?? 0))} más y la multa se paga sola.`
                    : 'Recarga el monto de la multa en tu billetera y se paga sola. Puedes usar tarjeta o transferencia.'}
                </Text>
              </>
            )}
            {pagada && (
              <Text style={[styles.fineNote, { color: '#1B7F3B', textAlign: 'center', fontWeight: '700' }]}>
                Multa pagada. Estamos revisando tu cuenta para reactivarla.
              </Text>
            )}
            {/* Igual: «Resolver por WhatsApp» es la via para pagar la multa
                fuera de la app, y en iOS no puede existir como llamada a la
                accion. Se sustituye por una frase que solo informa de donde
                esta la gestion, sin enlace, sin numero y sin cifra. */}
            {COBROS_EN_LA_APP ? (
              <Button3D title="Resolver por WhatsApp" color="#25D366" onPress={contactarSoporte} />
            ) : (
              !pagada && (
                <Text style={[styles.fineNote, { color: colors.textSecondary, textAlign: 'center' }]}>
                  Puedes resolver esta multa desde tu cuenta de Go Safely.
                </Text>
              )
            )}
          </View>

          {/* SALIDA OBLIGATORIA — sin esto el overlay es una cárcel: pantalla
              completa, zIndex 100000 y ningún control para volver. Google lo
              trata como funcionalidad rota. El bloqueo REAL sigue estando en
              firestore.rules (no puede pedir viajes ni ofertar), así que
              dejarlo cerrar la ventana no le abre ninguna puerta: solo le
              permite moverse por la app y llegar a soporte. */}
          <TouchableOpacity
            onPress={() => setBlocked(false)}
            style={{ paddingVertical: 14, alignItems: 'center' }}
            accessibilityRole="button"
            accessibilityLabel="Cerrar y volver a la app"
          >
            <Text style={{ color: colors.textSecondary, fontSize: 15, textDecorationLine: 'underline' }}>
              Cerrar y volver
            </Text>
          </TouchableOpacity>

          <Text style={[styles.footer, { color: colors.textSecondary }]}>
            Ganamos todos, pero también nos respetamos. 🤝
          </Text>
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100000,
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    maxHeight: '85%',
    borderRadius: 24,
    padding: 24,
  },
  iconCircle: {
    alignSelf: 'center',
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#E53935',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: { fontSize: 22, fontWeight: '900', textAlign: 'center', marginBottom: 8 },
  subtitle: { fontSize: 14, textAlign: 'center', lineHeight: 20, marginBottom: 18 },
  fineBox: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    marginBottom: 18,
  },
  fineLabel: { fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  fineAmount: { fontSize: 34, fontWeight: '900', color: '#E53935', marginVertical: 4 },
  fineNote: { fontSize: 12, textAlign: 'center' },
  payingText: { textAlign: 'center', marginTop: 8, fontSize: 13 },
  footer: { fontSize: 12, textAlign: 'center', marginTop: 16, fontStyle: 'italic' },
});
